import { useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import MuiLanIcon from '@mui/icons-material/Lan';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import MuiSearchIcon from '@mui/icons-material/Search';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemButton from '@mui/material/ListItemButton';
import MuiSearchOffIcon from '@mui/icons-material/SearchOff';
import MuiChevronRightIcon from '@mui/icons-material/ChevronRight';

import type { StarPrinter, StarDiscoveryDiagnostics } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import { lanPrinterTitle } from './lan-address';
import type { ScanState } from './use-lan-printer-setup';

// ----------------------------------------------------------------------

interface LanDiscoveryPanelProps {
  scan: ScanState;
  currentDefaultId?: string | null;
  onSearch: () => void;
  onSelect: (printer: StarPrinter) => void;
  onAddByAddress: () => void;
}

/** "What the search looked at" — the first thing to read when nothing was found. */
function SearchDetails({ diagnostics }: { diagnostics: StarDiscoveryDiagnostics }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button size="small" color="inherit" onClick={() => setOpen((value) => !value)}>
        {open ? 'Hide search details' : 'Show search details'}
      </Button>
      <Collapse in={open}>
        <Stack spacing={0.5} sx={{ mt: 1, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {diagnostics.addressesProbed} addresses checked · {diagnostics.hostsResponding} answered
            {diagnostics.timedOut ? ' · stopped at the time limit' : ''}
          </Typography>
          {diagnostics.interfaces.map((adapter) => (
            <Typography
              key={`${adapter.name}-${adapter.address}`}
              variant="caption"
              color={adapter.swept ? 'text.primary' : 'text.secondary'}
            >
              {adapter.swept ? 'Searched' : 'Skipped'} {adapter.name} ({adapter.address}/
              {adapter.prefixLength}){adapter.reason ? ` — ${adapter.reason}` : ''}
            </Typography>
          ))}
          {diagnostics.interfaces.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No network adapter on this register could be searched.
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function LanDiscoveryPanel({
  scan,
  currentDefaultId,
  onSearch,
  onSelect,
  onAddByAddress,
}: LanDiscoveryPanelProps) {
  const searching = scan.phase === 'searching';

  return (
    <Stack spacing={2} sx={{ px: 3, pb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Searches this register’s own network for Star printers.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={searching}
          onClick={onSearch}
          startIcon={<MuiIcon icon={MuiSearchIcon} />}
          sx={{ flexShrink: 0 }}
        >
          {scan.phase === 'idle' ? 'Search' : 'Search again'}
        </Button>
      </Stack>

      {searching && (
        <Box sx={{ py: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
            Searching for printers… this can take up to 15 seconds.
          </Typography>
        </Box>
      )}

      {scan.phase === 'failed' && (
        <Alert severity="error" action={<Button onClick={onSearch}>Retry</Button>}>
          {scan.error}
        </Alert>
      )}

      {scan.phase === 'done' && scan.message && <Alert severity="info">{scan.message}</Alert>}

      {scan.phase === 'done' && scan.printers.length > 0 && (
        <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
          {scan.printers.map((printer) => {
            const registered = printer.identifier === currentDefaultId;
            return (
              <ListItem key={printer.identifier} disablePadding divider>
                <ListItemButton onClick={() => onSelect(printer)} sx={{ py: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main', mr: 2 }}>
                    <MuiIcon icon={MuiLanIcon} width={22} />
                  </Avatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">{lanPrinterTitle(printer)}</Typography>
                        {registered && (
                          <Chip label="Registered" size="small" color="primary" variant="soft" />
                        )}
                      </Stack>
                    }
                    secondary={printer.ipAddress ?? printer.connection.identifier}
                  />
                  <MuiIcon icon={MuiChevronRightIcon} sx={{ color: 'text.disabled' }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {scan.phase === 'done' && scan.printers.length === 0 && (
        <Stack alignItems="center" spacing={1} sx={{ py: 3, textAlign: 'center' }}>
          <MuiIcon icon={MuiSearchOffIcon} width={40} sx={{ color: 'text.disabled' }} />
          <Typography variant="subtitle1">No printers found on this network</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            Check the printer is switched on and plugged into the same network as this register. If
            it is on a different network, or you know its address, add it by IP address.
          </Typography>
          <Button variant="contained" onClick={onAddByAddress} sx={{ mt: 1 }}>
            Add by IP address
          </Button>
        </Stack>
      )}

      {scan.phase === 'done' && scan.diagnostics && (
        <SearchDetails diagnostics={scan.diagnostics} />
      )}
    </Stack>
  );
}
